import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";
import { BarChart3 } from "lucide-react";
import api from "@/lib/api";

/**
 * Mini-grafico ore lavorate per dipendente nell'intervallo selezionato.
 * Si auto-aggiorna quando dateFrom/dateTo cambiano.
 *
 * Props:
 *  - dateFrom: ISO date string (es. "2025-01-01")
 *  - dateTo: ISO date string
 *  - onSelectEmployee?: (employee_id) => void  // click su barra → filtra
 *  - selectedEmployee?: employee_id corrente (per evidenziarne la barra)
 */
const COLORS = [
  "#0044FF", "#3366FF", "#5577FF", "#6688FF", "#7799FF",
  "#88AAFF", "#99BBFF",
];

export default function HoursByEmployeeChart({
  dateFrom,
  dateTo,
  onSelectEmployee,
  selectedEmployee,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const { data: r } = await api.get("/rapportini/totals/by-employee", { params });
        if (cancel) return;
        const rows = (r?.per_dipendente || [])
          .filter((d) => (d.ore_tot || 0) > 0)
          .slice(0, 12) // top 12 per leggibilità
          .map((d) => ({
            id: d.employee_id,
            name: (d.employee_nome || "—").length > 18
              ? (d.employee_nome || "—").slice(0, 17) + "…"
              : d.employee_nome || "—",
            fullName: d.employee_nome,
            ore: Math.round((d.ore_tot || 0) * 10) / 10,
            giorni: d.giorni_lavorati || 0,
          }));
        setData(rows);
      } catch (e) {
        console.warn("[chart] failed to load by-employee totals:", e?.message);
        setData([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [dateFrom, dateTo]);

  if (!loading && data.length === 0) return null;

  return (
    <Card className="p-4 md:p-5" data-testid="hours-by-employee-chart">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-5 w-5 text-[#0044FF]" />
        <div className="font-heading font-bold text-base">
          Ore per dipendente nell&apos;intervallo
        </div>
        <div className="text-xs text-slate-500 ml-1">
          (top {data.length} · click su barra per filtrare)
        </div>
      </div>
      <div className="w-full h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            margin={{ top: 5, right: 8, left: 0, bottom: 36 }}
          >
            <XAxis
              dataKey="name"
              angle={-28}
              textAnchor="end"
              interval={0}
              tick={{ fontSize: 11 }}
              height={50}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              label={{ value: "h", position: "insideLeft", offset: 14, fontSize: 11 }}
            />
            <Tooltip
              formatter={(value, name, props) => [
                `${value} h · ${props.payload.giorni} gg`,
                props.payload.fullName,
              ]}
              labelFormatter={() => ""}
              contentStyle={{ fontSize: 12, borderRadius: 6 }}
            />
            <Bar
              dataKey="ore"
              radius={[4, 4, 0, 0]}
              cursor={onSelectEmployee ? "pointer" : "default"}
              onClick={(b) => onSelectEmployee?.(b.id)}
            >
              {data.map((entry, idx) => (
                <Cell
                  key={entry.id}
                  fill={
                    selectedEmployee === entry.id
                      ? "#FF6600"
                      : COLORS[idx % COLORS.length]
                  }
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
